import NotificationsOffOutlined from "@mui/icons-material/NotificationsOffOutlined";
import RestartAlt from "@mui/icons-material/RestartAlt";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import {
  Alert,
  Box,
  Button,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { mdHeight, xsHeight } from "components/AppBar";
import { ConfirmDialog } from "components/ConfirmDialog";
import { SaveButton } from "components/SaveButton";
import {
  MyNotificationPreferenceCatalogQuery,
  NotificationPreferenceScope,
  Permission,
} from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import { useCallback, useState, type ReactNode } from "react";

import { PREFERENCE_GRID_MIN_WIDTH } from "./AlertRow";
import { AlertSectionAccordion } from "./AlertSectionAccordion";
import { OverallChannelRow } from "./OverallChannelRow";
import { SECTION_HEADING_SX, WIDGET_SX } from "./preferenceProvenance";
import {
  usePreferenceActions,
  usePreferenceCatalogs,
  useExpandedSections,
} from "./preferencesPageState";
import {
  isParentChannelOn,
  PREFERENCE_ROW_CHANNELS,
  type NotificationPreferenceDraft,
  type NotificationPreferenceDraftController,
  type DraftParentSwitch,
} from "./useNotificationPreferenceDraft";

/**
 * The notification preferences page.
 *
 * Two tabs over the same query: "My preferences" resolves the reader's own view,
 * "Organization defaults" resolves what a member with no preferences of their own
 * inherits. They are separate query invocations rather than one read filtered
 * client-side, because the organization's answer genuinely differs from the
 * reader's wherever the reader has an override — and showing the reader's own rows
 * as the organization's is how an admin ends up saving their personal choices onto
 * everybody.
 *
 * Nothing on the page writes on click. Every switch moves a draft, and one Save
 * sends only the entries that moved. That is not just tidiness: writing a row for
 * an untouched notification would convert a value that was inheriting into a frozen
 * override, so a later change to an organization default would stop reaching
 * anyone who had ever opened this page.
 */

const RESET_CONFIRM_HEADER = "Reset your notification preferences?";
const RESET_CONFIRM_BODY =
  "Your personal choices will be replaced by your organization's current defaults. This can't be undone.";
const PRESET_CONFIRM_HEADER = "Apply the Standard preset?";
// Says what actually happens: the organization's own overrides are cleared so
// every alert follows Encamp's recommended posture, and keeps following it as
// those recommendations change. Members' own choices are named explicitly because
// an admin's first worry is whether this reaches into everyone's settings.
const PRESET_CONFIRM_BODY =
  "Your organization's own choices will be cleared so every alert follows Encamp's recommended posture, including future updates to it. Members who have set their own preferences keep them. This can't be undone.";

const UNMUTE_CONFIRM_HEADER = "Turn these alerts back on?";
/*
  Muting an alert never stopped its condition being recorded — the notification was
  written and simply reached nobody — so switching it back on hands over everything
  still open.
*/
const UNMUTE_CONFIRM_BODY: Record<NotificationPreferenceScope, string> = {
  [NotificationPreferenceScope.My]:
    "Some of these cover problems at your facilities that are still open. There could be a lot of notifications at once, are you sure you want to turn these alerts back on?",
  [NotificationPreferenceScope.OrgDefault]:
    "Some of these cover problems that are still open. There could be a lot of notifications at once, are you sure you want to turn these alerts back on?",
};

type CatalogData = MyNotificationPreferenceCatalogQuery["myNotificationPreferenceCatalog"];

/**
 * Tab-strip height. Shorter than MUI's 48px default, which left a band of empty
 * chrome above the first widget on a page whose whole job is a dense table.
 */
const TAB_BAR_HEIGHT = 44;

/** The white sub-nav bar the tabs and the scope action share. */
const TAB_BAR_SX = {
  px: 3,
  minHeight: TAB_BAR_HEIGHT,
  backgroundColor: "background.default",
  borderBottom: 1,
  borderColor: "divider",
} as const;

/** Tabs and their labels both take the bar's height, or MUI re-adds its own 48. */
const TABS_SX = {
  minHeight: TAB_BAR_HEIGHT,
  "& .MuiTab-root": { minHeight: TAB_BAR_HEIGHT },
} as const;

/**
 * How wide the content is allowed to get.
 *
 * The three control columns are fixed at 272px plus gaps, so everything past this
 * lands in the name column — which is why an uncapped page put a notification's
 * name and its switches at opposite ends of a wide monitor. With the links-to
 * column gone the fixed side is narrower, so this can be too: it leaves the name
 * column around 530px, enough for a title and a one-line description.
 */
const PANEL_MAX_WIDTH = 840;

/**
 * The widget's own heading, which names the layer being edited.
 *
 * Worth saying twice — the tab above already says it — because this is the heading
 * on the thing that actually changes, and an admin who edits the organization's
 * defaults while believing they are editing their own is the expensive mistake this
 * page can cause.
 */
const WIDGET_HEADING: Record<NotificationPreferenceScope, string> = {
  [NotificationPreferenceScope.My]: "My notification preferences",
  [NotificationPreferenceScope.OrgDefault]: "Org default preferences",
};

/** The two tabs, as the values the query and the mutations are keyed by. */
const TABS = [
  { scope: NotificationPreferenceScope.My, label: "My preferences" },
  { scope: NotificationPreferenceScope.OrgDefault, label: "Organization defaults" },
] as const;

/**
 * Ids pairing each tab with the panel it controls.
 *
 * Derived from the scope rather than generated, because the panel is a single
 * element that swaps its contents: its `id` and `aria-labelledby` have to change
 * with the selected tab, or a screen reader announces the panel as belonging to
 * whichever tab happened to render first.
 */
function tabDomId(scope: NotificationPreferenceScope): string {
  return `notification-preferences-tab-${scope}`;
}

function panelDomId(scope: NotificationPreferenceScope): string {
  return `notification-preferences-panel-${scope}`;
}

function LoadingState() {
  return (
    <Stack spacing={2}>
      <Skeleton variant="rectangular" width="100%" height={140} />
      <Skeleton variant="rectangular" width="100%" height={72} />
      <Skeleton variant="rectangular" width="100%" height={72} />
    </Stack>
  );
}

/**
 * What an organization with no configurable sections sees. Never an error: the
 * delivery switches beside it are still meaningful, and "we have not shipped
 * anything you can tune yet" is a true and unalarming thing to say.
 */
function NoSectionsState() {
  return (
    <Stack spacing={1} alignItems="center" sx={{ py: 6 }}>
      <NotificationsOffOutlined sx={{ fontSize: 40, color: "text.disabled" }} />
      <Typography variant="body1">
        No notification settings are available for your organization yet.
      </Typography>
    </Stack>
  );
}

/**
 * The one state this page can be left in where nothing it configures will ever reach
 * the reader: both channels off at the top.
 *
 * Called out rather than left to be inferred. Every switch below still shows its own
 * stored position — the mute silences without clearing, which is the feature — so the
 * page looks fully configured while delivering nothing, and the sections' own "Off"
 * markers are easy to read as a per-section choice rather than one setting above them
 * all.
 *
 * Info rather than warning: the reader chose this, deliberately and reversibly, and a
 * page that scolds them for using a control it offers is worse than one that just says
 * what is true. It is also the reason this is not the pumpkin used for unbuilt work
 * elsewhere in the product.
 */
function AllNotificationsOffNotice({
  channelParentSwitches,
  draft,
}: {
  channelParentSwitches: readonly DraftParentSwitch[];
  draft: NotificationPreferenceDraft;
}) {
  const anyChannelOn = PREFERENCE_ROW_CHANNELS.some((channel) =>
    isParentChannelOn(draft, channelParentSwitches, channel)
  );
  if (anyChannelOn) {
    return null;
  }

  return (
    <Alert
      severity="info"
      icon={<NotificationsOffOutlined fontSize="inherit" />}
      // A little more space below than above: the row beneath pulls its column labels
      // up with a negative margin, so equal margins here read as tighter underneath
      // than over.
      sx={{ mt: 2, mb: 1.5 }}
    >
      {/*
        The state first, in bold, then the consequence — which also names both
        channels, so the sentence carries the condition that produced it rather than
        leaving the reader to work out which switches "all" refers to.
      */}
      <b>All notifications are off.</b> No notifications will reach you while both in-app and email
      channels are turned off.
    </Alert>
  );
}

type TabPanelProps = {
  catalog: CatalogData | undefined;
  controller: NotificationPreferenceDraftController;
  scope: NotificationPreferenceScope;
  /** True while a write is in flight; see {@link PreferencesTabPanel}. */
  disabled: boolean;
};

function PreferencesTabPanel({ catalog, controller, scope, disabled }: TabPanelProps) {
  const { isExpanded, toggle } = useExpandedSections();

  if (!catalog) {
    return <LoadingState />;
  }

  return (
    <Paper elevation={0} sx={WIDGET_SX}>
      {/*
        "Notifications", never "alerts": the app calls them notifications everywhere
        else, and a settings page is the worst place to introduce a second word for
        the same object. Which layer is being edited comes from the scope — see
        {@link WIDGET_HEADING}.

        No subtitle: "Grouped by program area" described what the reader can already
        see, and the line it occupied pushed the first control down for nothing.
      */}
      <Typography variant="overline" color="text.secondary" sx={SECTION_HEADING_SX}>
        {WIDGET_HEADING[scope]}
      </Typography>

      <AllNotificationsOffNotice
        channelParentSwitches={catalog.channelParentSwitches}
        draft={controller.draft}
      />

      {/*
          The channel-wide switches, on the same grid and at the same inset as the
          rows inside every section, so each column reads straight down the page. The
          scroll wrapper matches the sections' for the same reason: if this row stayed
          put while they scrolled, the columns would come apart exactly when a reader
          most needs them aligned.

          Rendered even with no sections below. A tenant entitled to none can still be
          sent the kinds that belong to no section, so "reach me this way at all" is a
          question worth answering whether or not there is anything to tune.
        */}
      <Box sx={{ px: 3, overflowX: "auto" }}>
        <Box sx={{ minWidth: PREFERENCE_GRID_MIN_WIDTH }}>
          <OverallChannelRow
            channelParentSwitches={catalog.channelParentSwitches}
            draft={controller.draft}
            scope={scope}
            disabled={disabled}
            onToggle={controller.setToggle}
            sendHourLocal={catalog.emailDigest.sendHourLocal}
          />
        </Box>
      </Box>

      {catalog.sections.length === 0 ? (
        <NoSectionsState />
      ) : (
        /* Rendered in the order the API gave them; the server owns display order
             so every surface agrees on it. No spacing between them: they are bands
             inside one widget, and their own rules separate them. */
        <Stack>
          {catalog.sections.map((section) => (
            <AlertSectionAccordion
              key={section.groupId}
              section={section}
              channelParentSwitches={catalog.channelParentSwitches}
              draft={controller.draft}
              scope={scope}
              expanded={isExpanded(section.groupId)}
              onToggleExpanded={() => toggle(section.groupId)}
              disabled={disabled}
              onToggle={controller.setToggle}
              onEmailChoice={controller.setEmailChoice}
              setSectionChannel={controller.setSectionChannel}
            />
          ))}
        </Stack>
      )}
    </Paper>
  );
}

/**
 * The page's write path: the three mutations, the in-flight flag every control
 * reads, and the handlers behind Save, Reset and the preset.
 *
 * Extracted as a hook because it is all branching — three mutations, each with a
 * refetch that can fail independently of the write that succeeded — and inlining it
 * pushed the component past both the complexity and function-length caps. The
 * component is left holding layout, which is what makes it readable.
 */
/**
 * Refreshes after a write that already succeeded, and says so if the refresh is
 * what failed.
 *
 * Its own hook because it is a distinct failure mode — the change landed, the page
 * did not catch up — and because leaving it inline pushed the actions hook past the
 * function-length cap.
 */
/**
 * The page's two destructive confirmations.
 *
 * Extracted together because they are the same kind of thing and neither reads any
 * of the page's state beyond what is passed here — and because the component that
 * held them had grown past the function-length cap, which is the signal to split.
 */
/**
 * The action beside the tab strip: the preset on the organization tab, the personal
 * reset on the reader's own.
 *
 * One control in one place, because it always acts on the tab it sits next to.
 *
 * Default (primary) colour, never `secondary`: this theme maps the secondary palette
 * to Encamp orange, which is reserved for staff-only surfaces. The one orange thing
 * on this customer-facing page is the "Encamp only" section chip.
 *
 * `describeChild` so the tooltip explains the button rather than renaming it —
 * without it MUI turns the title into an aria-label and the visible words stop being
 * the control's accessible name. The disabled preset button sits inside a span
 * because a disabled child fires no pointer events for the tooltip to hang off.
 */
/**
 * The unsaved-changes count with Cancel and Save.
 *
 * The count is stated rather than implied by an enabled button: this page can carry
 * a dozen edits across two tabs, and "3 unsaved changes" is the only thing that
 * tells a reader how much a Cancel would throw away.
 */
/**
 * What fills the tab panel: the load failure, the skeletons, or the table and its
 * footer.
 *
 * The error is fatal to the panel and never to the page. Returning only the alert
 * from the page took the tab strip with it, so a failed Organization-defaults query
 * removed the one control that got the reader back to My preferences and left
 * reloading as the only way out. The footer goes inside with it, because it acts on
 * this tab's catalog and there is no loaded catalog to act on.
 */
function PanelBody({
  errorMessage,
  loading,
  catalog,
  controller,
  scope,
  saving,
  mutating,
  scopeAction,
  onSave,
}: {
  errorMessage: string | undefined;
  loading: boolean;
  catalog: CatalogData | undefined;
  controller: NotificationPreferenceDraftController;
  scope: NotificationPreferenceScope;
  saving: boolean;
  mutating: boolean;
  scopeAction: ReactNode;
  onSave: () => Promise<void>;
}) {
  if (errorMessage) {
    return <Alert severity="error">Failed to load notification preferences: {errorMessage}</Alert>;
  }

  return (
    /* The cap lives here so the widget and the action row below it share one edge. */
    <Box sx={{ maxWidth: PANEL_MAX_WIDTH }}>
      {loading && !catalog ? (
        <LoadingState />
      ) : (
        <PreferencesTabPanel
          catalog={catalog}
          controller={controller}
          scope={scope}
          disabled={mutating}
        />
      )}

      <SaveFooter
        dirtyCount={controller.dirtyCount}
        saving={saving}
        mutating={mutating}
        scopeAction={scopeAction}
        onCancel={controller.revert}
        onSave={onSave}
      />
    </Box>
  );
}

function SaveFooter({
  dirtyCount,
  saving,
  mutating,
  scopeAction,
  onCancel,
  onSave,
}: {
  dirtyCount: number;
  saving: boolean;
  /** True for the whole write-and-refresh window; see {@link usePreferenceActions}. */
  mutating: boolean;
  /** The scope's own action — reset, or the preset — which sits at the far left. */
  scopeAction: ReactNode;
  onCancel: () => void;
  onSave: () => Promise<void>;
}) {
  return (
    /*
      One row under the widget: the scope action at the left edge, Save and Cancel at
      the right. Both ends align with the container rather than the page because the
      row sits inside the same width cap — a Save button out at the window's edge
      belongs to nothing the reader can see.
    */
    <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 1.5, pb: 3 }}>
      {scopeAction}
      <Box sx={{ flexGrow: 1 }} />
      {dirtyCount > 0 && (
        <Typography variant="body2" color="text.secondary">
          {dirtyCount} unsaved change{dirtyCount === 1 ? "" : "s"}
        </Typography>
      )}
      <Button onClick={onCancel} disabled={dirtyCount === 0 || mutating}>
        Cancel
      </Button>
      <SaveButton loading={saving} disabled={dirtyCount === 0 || mutating} onClick={onSave} />
    </Stack>
  );
}

function ScopeAction({
  onOrganizationTab,
  presetDisabled,
  mutating,
  onOpenPreset,
  onOpenReset,
}: {
  onOrganizationTab: boolean;
  /** Nothing to apply until the organization catalog has loaded. */
  presetDisabled: boolean;
  mutating: boolean;
  onOpenPreset: () => void;
  onOpenReset: () => void;
}) {
  if (onOrganizationTab) {
    return (
      <Tooltip describeChild title="Set every alert to Encamp's recommended defaults">
        <span>
          <Button
            startIcon={<TuneOutlined />}
            disabled={presetDisabled || mutating}
            onClick={onOpenPreset}
          >
            Apply Standard preset
          </Button>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip describeChild title="Reset to organization defaults">
      <span>
        <Button startIcon={<RestartAlt />} disabled={mutating} onClick={onOpenReset}>
          Reset preferences
        </Button>
      </span>
    </Tooltip>
  );
}

/**
 * The sub-nav bar: the scope tabs on the left, the action for that scope on the
 * right.
 *
 * White with a divider beneath, the same treatment the facility overview uses, so
 * the strip reads as chrome belonging to the page rather than a widget floating on
 * the canvas. Full-bleed on purpose — the rule under it is what separates chrome
 * from content, and it has to reach both edges to do that.
 *
 * The scope's action used to share this row. It now sits under the widget beside
 * Save and Cancel, where it lines up with the container it acts on rather than with
 * the page edge. The empty Box holds the bar's height for a caller who gets no tabs.
 */
function ScopeTabBar({
  scope,
  showTabs,
  onScopeChange,
}: {
  scope: NotificationPreferenceScope;
  showTabs: boolean;
  onScopeChange: (scope: NotificationPreferenceScope) => void;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={TAB_BAR_SX}
    >
      {showTabs ? (
        <Tabs
          value={scope}
          onChange={(_event, value) => onScopeChange(value)}
          // Named, because the page has one other tab-like row of controls and a
          // screen-reader user landing on an unnamed tablist has to guess.
          aria-label="Notification preference scope"
          sx={TABS_SX}
        >
          {TABS.map((tab) => (
            <Tab
              key={tab.scope}
              value={tab.scope}
              label={tab.label}
              id={tabDomId(tab.scope)}
              aria-controls={panelDomId(tab.scope)}
            />
          ))}
        </Tabs>
      ) : (
        <Box />
      )}
    </Stack>
  );
}

function PreferenceConfirmDialogs({
  resetOpen,
  presetOpen,
  unmuteOpen,
  unmuteScope,
  resetting,
  applyingPreset,
  saving,
  onCloseReset,
  onClosePreset,
  onCloseUnmute,
  onConfirmReset,
  onConfirmPreset,
  onConfirmUnmute,
}: {
  resetOpen: boolean;
  presetOpen: boolean;
  unmuteOpen: boolean;
  unmuteScope: NotificationPreferenceScope;
  resetting: boolean;
  applyingPreset: boolean;
  saving: boolean;
  onCloseReset: () => void;
  onClosePreset: () => void;
  onCloseUnmute: () => void;
  onConfirmReset: () => Promise<void>;
  onConfirmPreset: () => Promise<void>;
  onConfirmUnmute: () => Promise<void>;
}) {
  return (
    <>
      <ConfirmDialog
        open={resetOpen}
        header={RESET_CONFIRM_HEADER}
        info={RESET_CONFIRM_BODY}
        cancelText="Cancel"
        confirmText="Reset preferences"
        loading={resetting}
        onClose={onCloseReset}
        // Returned rather than fired-and-forgotten: the dialog's own loading guard
        // is what stops a second click from resetting twice.
        onConfirm={onConfirmReset}
      />
      <ConfirmDialog
        open={presetOpen}
        header={PRESET_CONFIRM_HEADER}
        info={PRESET_CONFIRM_BODY}
        cancelText="Cancel"
        confirmText="Apply preset"
        loading={applyingPreset}
        onClose={onClosePreset}
        onConfirm={onConfirmPreset}
      />
      <ConfirmDialog
        open={unmuteOpen}
        header={UNMUTE_CONFIRM_HEADER}
        info={UNMUTE_CONFIRM_BODY[unmuteScope]}
        cancelText="Cancel"
        confirmText="Save changes"
        loading={saving}
        onClose={onCloseUnmute}
        onConfirm={onConfirmUnmute}
      />
    </>
  );
}

export function NotificationPreferencesPage() {
  const { tenantId, tenant } = useTenant();
  const { hasPermissions } = useAuthorization();

  useBreadcrumb([
    { label: tenant?.name ?? "Unknown Tenant" },
    { label: "Settings" },
    { label: "Notifications" },
  ]);

  // The same bar the write path enforces: setting an organization default is an
  // org-wide act. Offering a tab that could only fail is worse than not offering
  // it, so the tab strip disappears entirely for everyone else.
  const canManageOrganizationDefaults = hasPermissions([Permission.WriteAllFacility]);

  const [scope, setScope] = useState<NotificationPreferenceScope>(NotificationPreferenceScope.My);
  const [resetOpen, setResetOpen] = useState(false);
  const [presetOpen, setPresetOpen] = useState(false);
  // Stable identities, so the write-path handlers are not rebuilt every render.
  const closeResetDialog = useCallback(() => setResetOpen(false), []);
  const closePresetDialog = useCallback(() => setPresetOpen(false), []);

  const {
    myQuery,
    organizationCatalog,
    onOrganizationTab,
    effectiveScope,
    activeCatalog,
    activeController,
    activeQuery,
    refetchBoth,
  } = usePreferenceCatalogs({ tenantId, scope, canManageOrganizationDefaults });

  const {
    mutating,
    saving,
    resetting,
    applyingPreset,
    handleSave,
    handleReset,
    handleApplyPreset,
    unmuteWarningOpen,
    closeUnmuteWarning,
    confirmSaveAfterWarning,
  } = usePreferenceActions({
    tenantId,
    // The effective scope, never the raw tab state: a save has to write the layer the
    // draft actually came from.
    scope: effectiveScope,
    activeController,
    sections: activeCatalog?.sections,
    channelParentSwitches: activeCatalog?.channelParentSwitches,
    refetchBoth,
    refetchMine: myQuery.refetch,
    closeReset: closeResetDialog,
    closePreset: closePresetDialog,
  });

  // Fatal to the panel, never to the page. Returning only the alert took the tab
  // strip with it, so a failed Organization-defaults query removed the one control
  // that gets the reader back to My preferences and left reloading as the only way
  // out. The action rows go inside the panel with it, because they act on this
  // tab's catalog and there is no loaded catalog to act on.
  const errorMessage = activeQuery.error?.message;

  /**
   * The panel's half of the tab pairing, applied only when the tab strip is
   * actually rendered — a `tabpanel` with no tab above it is a role pointing at
   * nothing.
   */
  const panelTabAria = canManageOrganizationDefaults
    ? { role: "tabpanel", id: panelDomId(scope), "aria-labelledby": tabDomId(scope) }
    : {};

  return (
    /*
      Gray canvas with white widgets on it, the same shell the waste and facility
      pages use. The page had no page-level background and no margins, so every
      section ran into the next as one undifferentiated white slab — which read as
      a broken page rather than a dense one.

      No page title: the breadcrumb above already says "Settings / Notifications",
      and the tab strip is what names the two views. A heading here repeated the
      breadcrumb and pushed the first widget below the fold for no information.
    */
    <Box
      sx={{
        backgroundColor: (theme) => theme.palette.canvas,
        /*
          Two parts, because neither alone is enough.

          The `minHeight` keeps a short page looking deliberate rather than a band of
          canvas floating on white. It cannot reach the bottom on its own: this box
          starts below the breadcrumb, so subtracting only the nav leaves it short by
          the breadcrumb's height, and subtracting both would hard-code a second
          element's size into this page.

          The shadow does the filling. It paints a copy of this box's own colour far
          below it, which covers whatever gap is left without affecting layout or
          adding to the scroll area — so the canvas reaches the bottom of any window
          and nothing here has to know what sits above it.
        */
        minHeight: {
          xs: (theme) => `calc(100vh - ${theme.spacing(xsHeight)})`,
          md: (theme) => `calc(100vh - ${theme.spacing(mdHeight)})`,
        },
        boxShadow: (theme) => `0 100vh 0 100vh ${theme.palette.canvas}`,
        clipPath: "inset(0 0 -100vh 0)",
      }}
    >
      {/*
        Tabs and the scope action share one row: the action always acts on the tab
        beside it, so putting them on separate rows made the relationship guesswork
        and wasted a band of vertical space. `minHeight` holds the row steady for a
        caller who gets no tabs at all.
      */}
      {/*
        White bar with a divider beneath, the same sub-nav treatment the facility
        overview uses — the tab strip reads as chrome belonging to the page rather
        than as a widget floating on the canvas. Full-bleed on purpose: the rule
        under it is what separates chrome from content, and it has to reach both
        edges to do that.
      */}
      <ScopeTabBar
        scope={scope}
        showTabs={canManageOrganizationDefaults}
        onScopeChange={setScope}
      />

      <Box {...panelTabAria} sx={{ px: 3, pt: 2.5, pb: 4 }}>
        <PanelBody
          scopeAction={
            <ScopeAction
              onOrganizationTab={onOrganizationTab}
              presetDisabled={!organizationCatalog}
              mutating={mutating}
              onOpenPreset={() => setPresetOpen(true)}
              onOpenReset={() => setResetOpen(true)}
            />
          }
          errorMessage={errorMessage}
          loading={activeQuery.loading}
          catalog={activeCatalog}
          controller={activeController}
          scope={effectiveScope}
          saving={saving}
          mutating={mutating}
          onSave={handleSave}
        />
      </Box>

      <PreferenceConfirmDialogs
        resetOpen={resetOpen}
        presetOpen={presetOpen}
        unmuteOpen={unmuteWarningOpen}
        unmuteScope={effectiveScope}
        resetting={resetting}
        applyingPreset={applyingPreset}
        saving={saving}
        onCloseReset={() => setResetOpen(false)}
        onClosePreset={() => setPresetOpen(false)}
        onCloseUnmute={closeUnmuteWarning}
        onConfirmReset={handleReset}
        onConfirmPreset={handleApplyPreset}
        onConfirmUnmute={confirmSaveAfterWarning}
      />
    </Box>
  );
}
